import { FormEvent, useEffect, useRef, useState } from 'react';
import type { AdminPlanCode, AdminSignupRow, SignupSettings } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';

// Öz qeydiyyatı və sınaq (KT-85): yuxarıda «Sınaq» tənzimləməsi (dəyişmək yalnız əsas adminə), altında «Yeni qeydiyyatlar»
// süzgəclə. Təsdiq gözləyənə — Təsdiqlə / Rədd et; sınaqda və bitəndə — Uzat / Paketə keçir / Bitir (abunə redaktorunun
// eyni API-si; endirim və əlavələr saxlanır, yalnız billing bölməsi ilə). Silmə yoxdur.

const PLANS: [AdminPlanCode, string][] = [['simple', 'Sadə'], ['standard', 'Standart'], ['full', 'Tam'], ['custom', 'Özün qur']];
const SECTOR: Record<AdminSignupRow['sector'], string> = {
  market: 'Market', wholesale: 'Topdan', agro: 'Aqro', restaurant: 'Restoran', clothing: 'Geyim', service: 'Xidmət',
};
const FILTERS: [string, string][] = [
  ['', 'Hamısı'], ['pending', 'Təsdiq gözləyir'], ['invited', 'Link göndərilib'], ['trial', 'Sınaqda'], ['ended', 'Sınağı bitənlər'], ['rejected', 'Rədd'],
];
const STATUS: Record<AdminSignupRow['status'], string> = {
  pending: 'Təsdiq gözləyir', invited: 'Link göndərilib', registered: 'Qeydiyyat olunub', rejected: 'Rədd edilib',
};

function errText(err: unknown): string {
  if (err instanceof AdminApiError) return err.message;
  // Öz yoxlamalarımız (məs. abunə yoxdur) — şəbəkə xətası kimi göstərilmir
  if (err instanceof Error && !(err instanceof TypeError)) return err.message;
  return 'Şəbəkə xətası. Yenidən cəhd edin.';
}

function baku(iso: string): string {
  return new Date(Date.parse(iso) + 4 * 3600_000).toISOString().slice(0, 16).replace('T', ' ');
}

/** Bakı tarixi (YYYY-MM-DD) + gün */
function bakuDay(addDays: number): string {
  return new Date(Date.now() + 4 * 3600_000 + addDays * 86_400_000).toISOString().slice(0, 10);
}

function dmy(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

export function Signups({ isOwner, canBilling }: { isOwner: boolean; canBilling: boolean }) {
  const [settings, setSettings] = useState<SignupSettings | null>(null);
  const [draft, setDraft] = useState<SignupSettings | null>(null);
  // Xatırlatma günləri mətn kimi yazılır (vergül silinməsin), saxlayanda oxunur
  const [remindText, setRemindText] = useState('');
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<AdminSignupRow[] | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  function load(f = filter) {
    const n = ++seq.current;
    adminApi
      .signups(f)
      .then((r) => n === seq.current && setRows(r))
      .catch((err) => n === seq.current && setMsg({ ok: false, text: errText(err) }));
  }
  useEffect(() => {
    adminApi
      .signupSettings()
      .then((s) => {
        setSettings(s);
        setDraft(s);
        setRemindText(s.remindDays.join(', '));
      })
      .catch((err) => setMsg({ ok: false, text: errText(err) }));
  }, []);
  useEffect(() => load(filter), [filter]);

  // fn null qaytarırsa — istifadəçi imtina etdi (təsdiq pəncərəsi): mesaj və yenidən yükləmə yoxdur
  async function run(fn: () => Promise<string | null>) {
    setBusy(true);
    setMsg(null);
    try {
      const text = await fn();
      if (text === null) return;
      setMsg({ ok: true, text });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    const days = remindText.split(/[,\s]+/).filter(Boolean).map(Number);
    if (days.some((n) => !Number.isInteger(n) || n < 1 || n > 30) || days.length > 5) {
      setMsg({ ok: false, text: 'Xatırlatma günləri: 1–30 arası tam ədədlər, vergüllə, ən çox 5 (məs. 3, 1)' });
      return;
    }
    const next = { ...draft, remindDays: [...new Set(days)].sort((a, b) => b - a) };
    await run(async () => {
      await adminApi.signupSettingsSet(next);
      setSettings(next);
      setDraft(next);
      setRemindText(next.remindDays.join(', '));
      return next.enabled ? 'Saxlanıldı. Öz qeydiyyatı açıqdır.' : 'Saxlanıldı. Öz qeydiyyatı bağlıdır.';
    });
  }

  // Abunə redaktorunun API-si: endirim və əlavələr olduğu kimi qalır
  function subscription(r: AdminSignupRow, status: 'trial' | 'active', trialUntil?: string) {
    if (!r.tenantId || !r.outletId || !r.planCode) throw new Error('Bu qeydiyyatın abunəsi tapılmadı — Obyektlər bölməsindən dəyişin');
    return adminApi.subscriptionSet(r.tenantId, r.outletId, {
      plan: r.planCode,
      status,
      trialUntil,
      discountPct: r.discountPct ?? '0',
      addons: (r.addons ?? {}) as Record<string, number>,
    });
  }

  const set = <K extends keyof SignupSettings>(k: K, v: SignupSettings[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const dirty = !!draft && !!settings && (JSON.stringify(draft) !== JSON.stringify(settings) || remindText !== settings.remindDays.join(', '));

  return (
    <>
      <section className="card card-wide" aria-labelledby="sinaq-title">
        <div className="toolbar">
          <h2 id="sinaq-title">Sınaq</h2>
          <span className="topbar-spacer" />
          {settings && <span className={settings.enabled ? 'badge badge-ok' : 'badge badge-off'}>{settings.enabled ? 'Öz qeydiyyatı açıq' : 'Öz qeydiyyatı bağlı'}</span>}
        </div>
        {draft && (
          <form className="form-stack" onSubmit={save} noValidate>
            <fieldset className="limit-grid" disabled={!isOwner || busy}>
              <label className="check">
                <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} /> Öz qeydiyyatı açıq («Pulsuz sınaq»)
              </label>
              <label className="field">
                Sınaq, gün
                <input inputMode="numeric" value={String(draft.trialDays)} onChange={(e) => set('trialDays', Number(e.target.value.replace(/\D/g, '')) || 0)} />
              </label>
              <label className="field">
                Sınaq paketi
                <select value={draft.plan} onChange={(e) => set('plan', e.target.value as AdminPlanCode)}>
                  {PLANS.map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                Təsdiq
                <select value={draft.approval} onChange={(e) => set('approval', e.target.value as SignupSettings['approval'])}>
                  <option value="auto">Avtomatik (link dərhal gedir)</option>
                  <option value="admin">Admin təsdiqindən sonra</option>
                </select>
              </label>
              <label className="field">
                Xatırlatma, sınaq bitməzdən … gün əvvəl
                <input value={remindText} onChange={(e) => setRemindText(e.target.value)} placeholder="3, 1" />
              </label>
            </fieldset>
            {isOwner ? (
              <div className="form-actions">
                <button type="submit" className="btn-primary btn-small" disabled={busy || !dirty}>
                  Saxla
                </button>
              </div>
            ) : (
              <p className="muted small">Tənzimləməni yalnız əsas admin dəyişir.</p>
            )}
            <p className="muted small">Sınaq bitəndə kassada satış dayanmır: sahibə məktub gedir və yuxarıda «Sınaq bitdi — paket seçin» zolağı görünür.</p>
          </form>
        )}
      </section>

      <section className="card card-wide" aria-labelledby="signups-title">
        <div className="toolbar">
          <h2 id="signups-title">Yeni qeydiyyatlar</h2>
          <span className="topbar-spacer" />
          <label className="field field-inline">
            <span className="sr-only">Süzgəc</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Süzgəc">
              {FILTERS.map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </label>
        </div>
        {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}
        {rows && rows.length === 0 && <p className="muted">Bu süzgəcdə qeydiyyat yoxdur.</p>}
        {rows && rows.length > 0 && (
          <div className="table-scroll">
            <table className="plain">
              <thead>
                <tr>
                  <th>Şirkət · sahib</th>
                  <th>Status</th>
                  <th className="narrow-hide">Sınaq</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const inTrial = r.subStatus === 'trial' && !!r.trialUntil;
                  const ended = inTrial && r.trialUntil! < bakuDay(0);
                  return (
                    <tr key={r.id}>
                      <td>
                        <strong>{r.tenantName ?? r.companyName}</strong>
                        <div className="muted small">{r.ownerName}</div>
                        <div className="muted small">{r.email}</div>
                        <div className="muted small">{r.phone}</div>
                        <div className="muted small">
                          {SECTOR[r.sector]}
                          {r.voen ? ` · VÖEN ${r.voen}` : ''} · {baku(r.createdAt)}
                        </div>
                        <div className="row-actions">
                        {r.status === 'pending' && isOwner && (
                          <>
                            <button type="button" className="btn-primary btn-small" disabled={busy} onClick={() => run(async () => (await adminApi.signupApprove(r.id), `Təsdiqləndi — link ${r.email} ünvanına göndərildi.`))}>
                              Təsdiqlə
                            </button>{' '}
                            <button
                              type="button"
                              className="btn-small"
                              disabled={busy}
                              onClick={() => {
                                const note = window.prompt('Rəddin səbəbi (könüllü)') ?? null;
                                if (note === null) return;
                                void run(async () => (await adminApi.signupReject(r.id, note), 'Müraciət rədd edildi.'));
                              }}
                            >
                              Rədd et
                            </button>
                          </>
                        )}
                        {inTrial && canBilling && (
                          <>
                            <button
                              type="button"
                              className="btn-small"
                              disabled={busy}
                              onClick={() => {
                                const until = (ended ? bakuDay(0) : r.trialUntil!) > bakuDay(0) ? r.trialUntil! : bakuDay(0);
                                const next = new Date(Date.parse(`${until}T00:00:00Z`) + 7 * 86_400_000).toISOString().slice(0, 10);
                                void run(async () => (await subscription(r, 'trial', next), `Sınaq ${dmy(next)}-dək uzadıldı.`));
                              }}
                            >
                              Uzat (+7 gün)
                            </button>{' '}
                            <button
                              type="button"
                              className="btn-small"
                              disabled={busy}
                              onClick={() =>
                                // KT-106: bu ayın hesabı keçid günündən ayın sonuna qədər (gün payı); sınaq günləri pulsuz — məbləğ əvvəlcədən göstərilir
                                void run(async () => {
                                  if (!r.tenantId || !r.outletId) throw new Error('Bu qeydiyyatın abunəsi tapılmadı — Obyektlər bölməsindən dəyişin');
                                  const p = await adminApi.activationPreview(r.tenantId, r.outletId);
                                  const sum = (v: string) => `${Number(v).toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₼`;
                                  const text =
                                    `${dmy(p.from)}-dən paketə keçir. Bu ayın hesabı: ${p.days} / ${p.daysInMonth} gün — ${sum(p.total)} (tam ay ${sum(p.fullMonth)}).` +
                                    (ended ? '' : ` Sınaq ${dmy(r.trialUntil!)}-dək idi — qalan sınaq günləri bitir.`) +
                                    ' Davam edək?';
                                  if (!window.confirm(text)) return null;
                                  // Təsdiq gözlənərkən gün dəyişibsə (gecə yarısı) — göstərilən məbləğ köhnədir
                                  const again = await adminApi.activationPreview(r.tenantId, r.outletId);
                                  if (again.from !== p.from || again.total !== p.total) throw new Error('Məbləğ dəyişdi (gün keçdi) — yenidən «Paketə keçir» basın');
                                  await subscription(r, 'active');
                                  return `Paketə keçirildi: bu ay ${sum(p.total)}.`;
                                })
                              }
                            >
                              Paketə keçir
                            </button>{' '}
                            {!ended && (
                              <button type="button" className="btn-small" disabled={busy} onClick={() => run(async () => (await subscription(r, 'trial', bakuDay(-1)), 'Sınaq bitirildi.'))}>
                                Bitir
                              </button>
                            )}
                          </>
                        )}
                        </div>
                      </td>
                      <td>
                        {STATUS[r.status]}
                        {r.status === 'invited' && r.inviteStatus === 'pending' && r.inviteExpires && <div className="muted small nowrap">{baku(r.inviteExpires)}-dək</div>}
                        {r.rejectNote && <div className="muted small">{r.rejectNote}</div>}
                      </td>
                      <td className="narrow-hide small">
                        {inTrial ? (
                          <span className={ended ? 'bad' : undefined}>
                            {ended ? 'Bitib: ' : ''}
                            {dmy(r.trialUntil!)}
                          </span>
                        ) : r.subStatus === 'active' ? (
                          'Paketdədir'
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
